/**
 * @file envRegistry.ts
 *
 * Runtime registry mapping organizationId or environment values to their
 * corresponding env string. Populated at module-load time by plugin
 * registrations so that no core module needs hardcoded knowledge of
 * plugin-specific IDs.
 *
 * Example — in grabcadPlugin.ts:
 *   registerEnvironmentEnv('GrabCAD', 'grabcad');
 */
import { type UserData } from '../features/serverApi/asApi.ts';

/**
 * Env for the plain cloud app: what as-app falls back to when no plugin claims
 * the session. It carries no registered settings or providers of its own, so it
 * behaves exactly like an unset env — it just says so explicitly, rather than
 * leaving consumers to read a null as "not resolved yet".
 */
export const DEFAULT_ENV = 'default';

const _orgIdToEnv = new Map<number, string>();
const _environmentToEnv = new Map<string, string>();

let _markEnvReady: () => void;

/**
 * Resolves once as-app has settled `internalData.appEnv` — either from an
 * explicit `?env=`/attribute or from the userData fetch. Anything that branches
 * on the env must await this first: until it resolves `appEnv` is still null,
 * so an env check made too early silently reads as "not that env". See
 * GrabCADMixin's persistent-state methods for the case that motivated it (the
 * splash "don't show again" flag was written to GrabCAD's store but read back
 * from localStorage, because the read happened before the fetch landed).
 *
 * A promise rather than only the 'as-intrn-env-ready' event, because the
 * explicit-env path fires that event during element upgrade — before app code
 * gets a chance to attach a listener.
 */
export const envReady: Promise<void> = new Promise<void>(resolve => { _markEnvReady = resolve; });

/** Settles {@link envReady}. Called by as-app once the env is known. */
export function markEnvReady(): void {
  _markEnvReady();
}

/** Register an organizationId → env mapping. Called by plugin modules on load. */
export function registerOrgIdEnv(orgId: number, env: string): void {
  _orgIdToEnv.set(orgId, env);
}

/** Return the env string registered for the given organizationId, or undefined. */
export function getEnvForOrgId(orgId: number): string | undefined {
  return _orgIdToEnv.get(orgId);
}

/** Register an environment string → env mapping. Called by plugin modules on load. */
export function registerEnvironmentEnv(environment: string, env: string): void {
  _environmentToEnv.set(environment, env);
}

/** Return the env string registered for the given environment name, or undefined. */
export function getEnvForEnvironment(environment: string): string | undefined {
  return _environmentToEnv.get(environment);
}

/** Resolves the env for a userData object using whatever plugins have registered
 *  above — checks environment name first, then falls back to organizationId.
 *  Returns undefined if there's no userData or nothing registered matches it. */
export function resolveEnvFromUserData(userData: UserData | null | undefined): string | undefined {
  if (!userData) return undefined;

  if (userData.environment) {
    const env = getEnvForEnvironment(userData.environment);
    if (env) return env;
  }

  if (userData.organizationId !== undefined) {
    return getEnvForOrgId(userData.organizationId);
  }

  return undefined;
}
